import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getIntlCommittees,
  sendCommitteeMessage,
} from '../../../endpoints/chapters';
import { StandingCommitteesResponse } from '../../../endpoints/chaptersTs';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import { getGroupRolesByType } from '../../../endpoints/groups';
import { InternationalCommitteeMessage } from '../../../types/chapters';
import {
  INTL_GROUP_TYPE_STANDING_COMMITTEES,
  IntlSaveData,
  saveIntlLeadershipPosition,
} from './intlLeadershipEdit';
import AlertItem from '../../../Components/AlertItem';

const IntlCommittees: React.FC = () => {
  let [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState<string | null>(
    null
  );
  const [selectedCommitteeName, setSelectedCommitteeName] =
    useState<string>('');
  const [messageText, setMessageText] = useState('');

  const { data: committeesResponse, isFetching } =
    useApiGet<StandingCommitteesResponse>(
      ['intl-committees'],
      getIntlCommittees,
      {
        enabled: true,
        retry: 1,
      },
      {}
    );

  const canEdit = Boolean(committeesResponse?.can_edit);
  const committeesData = committeesResponse?.data || [];

  const { data: committeeRoles } = useApiGet(
    ['intl-committee-roles'],
    () => getGroupRolesByType(INTL_GROUP_TYPE_STANDING_COMMITTEES),
    { enabled: canEdit, retry: 1 },
    {}
  );

  const filteredCommittees = committeesData.filter((committee) =>
    committee.name.toLowerCase().includes(search.toLowerCase())
  );

  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    sendCommitteeMessage,
    () => {
      setMessageDialogOpen(false);
      setMessageText('');
      setSelectedCommitteeId(null);
      setSelectedCommitteeName('');
    },
    (error) => {
      setErrorSaving(error?.message || 'Error sending message');
    }
  );

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleMessageClick = (committee: { id: string; name: string }) => {
    setSelectedCommitteeId(committee.id);
    setSelectedCommitteeName(committee.name);
    setMessageDialogOpen(true);
  };

  const handleSendMessage = () => {
    if (selectedCommitteeId && messageText.trim()) {
      const messageData: InternationalCommitteeMessage = {
        committee_id: selectedCommitteeId,
        user_id: null,
        message: messageText.trim(),
      };
      sendMessage(messageData);
    }
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageText('');
    setSelectedCommitteeId(null);
    setSelectedCommitteeName('');
  };

  const onSavePosition = (position: IntlSaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    void saveIntlLeadershipPosition(position)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['intl-committees'] });
      })
      .catch((err: { message?: string }) => {
        setErrorSaving(err?.message || 'Failed to save position.');
      })
      .finally(() => setIsSaving(false));
  };

  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!committeesResponse) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      {errorSaving && (
        <Grid item xs={12} sx={{ px: 2, pt: 2 }}>
          <AlertItem
            severity="error"
            description={errorSaving}
            onClose={() => setErrorSaving(null)}
          />
        </Grid>
      )}
      <Grid item xs={12} sx={{ p: 2 }}>
        <TextField
          label="Search"
          id="search-field"
          size="small"
          value={search}
          fullWidth={true}
          onChange={(event) => handleSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {filteredCommittees.map((committee) => (
        <Grid item xs={12} md={6} sx={{ p: 2 }} key={committee.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">{committee.name}</Typography>
                  {committee.is_project && (
                    <Button
                      onClick={() => handleMessageClick(committee)}
                      variant="contained"
                      size="small"
                      sx={{
                        fontWeight: 'bold',
                        pb: 0,
                        pt: 0,
                        pl: 1,
                        pr: 1,
                        ml: 1,
                      }}
                    >
                      Message
                    </Button>
                  )}
                </Box>
              }
              subheader={committee.description}
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {(committee.members || [])
                  .slice()
                  .sort((a, b) => a.order - b.order)
                  .map((user) => (
                    <PositionDisplay
                      id={user.id}
                      position={user.position || ''}
                      positionId={user.position_id || ''}
                      committeeId={
                        user.committee_id || user.group_id || committee.id
                      }
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={canEdit}
                      canMessage={!committee.is_project}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={canEdit ? onSavePosition : null}
                      userId={user.user_id}
                      pageFrom={'intl-committees'}
                      key={user.id}
                    />
                  ))}
                {canEdit && (
                  <Grid item xs={12} sx={{ p: 1 }}>
                    <AddPosition
                      buttonText="Add Position"
                      committeeId={committee.id}
                      positions={(committeeRoles || []).map((r: any) => ({
                        id: r.id,
                        name: r.name,
                        order: r.order,
                      }))}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                      saveSuccess={null}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}

      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message to {selectedCommitteeName}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Enter your message..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!messageText.trim() || isSendingMessage}
          >
            {isSendingMessage ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default IntlCommittees;
